import {
  AlertCircle,
  BarChart3,
  Bell,
  CalendarDays,
  ChevronsUpDown,
  CircleUser,
  FileText,
  GitBranch,
  Inbox,
  LayoutList,
  ListChecks,
  MessageSquare,
  MousePointer2,
  PanelRight,
  Search,
  SlidersHorizontal,
  Square,
  SquareCheck,
  Table,
  Type,
} from "lucide-react"
import * as React from "react"
import { Link } from "react-router"

import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

type Category = "input" | "action" | "overlay" | "feedback" | "data" | "display"

interface PrimitiveEntry {
  slug: string
  name: string
  note: string
  category: Category
  icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>
}

const CATEGORY_LABEL: Record<Category, string> = {
  input: "Inputs",
  action: "Actions",
  overlay: "Overlays",
  feedback: "Feedback",
  data: "Data",
  display: "Display",
}

const CATEGORY_ORDER: Category[] = [
  "input",
  "action",
  "overlay",
  "feedback",
  "data",
  "display",
]

const PRIMITIVES: PrimitiveEntry[] = [
  { slug: "button", name: "Button", note: "Primary, default, ghost, destructive", category: "action", icon: MousePointer2 },
  { slug: "icon-button", name: "IconButton", note: "Icon-only, aria-label required", category: "action", icon: Square },
  { slug: "input", name: "Input", note: "Text input with error + hint", category: "input", icon: Type },
  { slug: "textarea", name: "Textarea", note: "Multi-line, auto-grow", category: "input", icon: Type },
  { slug: "form-field", name: "FormField", note: "Labeled control wrapper", category: "input", icon: LayoutList },
  { slug: "select", name: "Select", note: "Single-value dropdown", category: "input", icon: ChevronsUpDown },
  { slug: "combobox", name: "Combobox", note: "Searchable select", category: "input", icon: Search },
  { slug: "date-picker", name: "DatePicker", note: "Popover calendar, single + range", category: "input", icon: CalendarDays },
  { slug: "dialog", name: "Dialog", note: "Modal — one action per screen", category: "overlay", icon: SquareCheck },
  { slug: "sheet", name: "Sheet", note: "Side drawer", category: "overlay", icon: PanelRight },
  { slug: "popover", name: "Popover", note: "Anchored floating panel", category: "overlay", icon: MessageSquare },
  { slug: "alert", name: "Alert", note: "Inline notice, four severities", category: "feedback", icon: AlertCircle },
  { slug: "toast", name: "Toast", note: "Sonner-based notifications", category: "feedback", icon: Bell },
  { slug: "empty-state", name: "EmptyState", note: "No results, first-run states", category: "feedback", icon: Inbox },
  { slug: "data-table", name: "DataTable", note: "Expanded rows, selection, measured widths", category: "data", icon: Table },
  { slug: "table-sort", name: "Sort", note: "Sort trigger + panel for tables", category: "data", icon: SlidersHorizontal },
  { slug: "pagination", name: "Pagination", note: "Table pager", category: "data", icon: ChevronsUpDown },
  { slug: "stat-card", name: "StatCard", note: "Inert + interactive (queue filter)", category: "display", icon: BarChart3 },
  { slug: "version-timeline", name: "VersionTimeline", note: "Absolute-positioned stems", category: "display", icon: GitBranch },
  { slug: "avatar", name: "Avatar", note: "Hash-to-hue, AvatarGroup, LiveIndicator", category: "display", icon: CircleUser },
]

const CATEGORY_TINT: Record<Category, string> = {
  input: "bg-[color:var(--info)]/10 text-[color:var(--info)]",
  action: "bg-[color:var(--brand)]/10 text-[color:var(--brand)]",
  overlay: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
  feedback: "bg-[color:var(--warn)]/10 text-[color:var(--warn)]",
  data: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  display: "bg-slate-500/10 text-slate-600 dark:text-slate-300",
}

export function PrimitivesIndexRoute(): JSX.Element {
  const [query, setQuery] = React.useState("")
  const [active, setActive] = React.useState<Category | "all">("all")

  const q = query.trim().toLowerCase()
  const filtered = PRIMITIVES.filter((p) => {
    if (active !== "all" && p.category !== active) return false
    if (!q) return true
    return (
      p.name.toLowerCase().includes(q) ||
      p.note.toLowerCase().includes(q) ||
      p.slug.includes(q)
    )
  })

  const grouped = CATEGORY_ORDER.map((cat) => ({
    cat,
    items: filtered.filter((p) => p.category === cat),
  })).filter((g) => g.items.length > 0)

  const counts = React.useMemo(() => {
    const map = new Map<Category | "all", number>()
    map.set("all", PRIMITIVES.length)
    for (const cat of CATEGORY_ORDER) {
      map.set(cat, PRIMITIVES.filter((p) => p.category === cat).length)
    }
    return map
  }, [])

  return (
    <div className="flex h-full w-full flex-col gap-6 p-6">
      <header className="flex flex-col gap-3">
        <h1 className="text-display text-text">Component library</h1>
        <p className="max-w-3xl text-body text-text-2">
          Every UI primitive under{" "}
          <code className="font-id text-body-sm text-text">src/components/ui/</code>{" "}
          has a live preview here. Use these as the building blocks for new
          screens — search by name or category, open a card for the full
          spec, states and code sample.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-xs">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-text-3"
            aria-hidden="true"
          />
          <Input
            aria-label="Filter components"
            placeholder="Filter…"
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            className="pl-8"
          />
        </div>

        <div className="flex flex-wrap items-center gap-1.5" role="tablist" aria-label="Category">
          <FilterChip
            label="All"
            count={counts.get("all") ?? 0}
            active={active === "all"}
            onClick={() => setActive("all")}
          />
          {CATEGORY_ORDER.map((cat) => (
            <FilterChip
              key={cat}
              label={CATEGORY_LABEL[cat]}
              count={counts.get(cat) ?? 0}
              active={active === cat}
              onClick={() => setActive(cat)}
            />
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center rounded-lg border border-dashed border-border bg-surface p-10 text-center">
          <p className="text-heading text-text">No matches</p>
          <p className="mt-1 text-body-sm text-text-3">
            Nothing matched <code className="font-id text-text-2">{query}</code>.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          {grouped.map(({ cat, items }) => (
            <section key={cat} aria-labelledby={`section-${cat}`}>
              <div className="mb-3 flex items-baseline gap-3">
                <h2
                  id={`section-${cat}`}
                  className="font-id text-caption uppercase tracking-[0.16em] text-text-3"
                >
                  {CATEGORY_LABEL[cat]}
                </h2>
                <span className="text-caption tabular-nums text-text-3">
                  {items.length}
                </span>
              </div>
              <ul className="grid gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
                {items.map((p) => (
                  <li key={p.slug}>
                    <PrimitiveCard entry={p} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}

function PrimitiveCard({ entry }: { entry: PrimitiveEntry }): JSX.Element {
  const Icon = entry.icon
  return (
    <Link
      to={`/dev/primitives/${entry.slug}`}
      title={entry.note}
      className={cn(
        "group flex h-full items-start gap-2.5 rounded-md border border-border bg-surface px-2.5 py-2 transition-colors duration-120",
        "hover:border-border-strong hover:bg-surface-sunken",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
      )}
    >
      <span
        className={cn(
          "grid size-7 shrink-0 place-items-center rounded",
          CATEGORY_TINT[entry.category],
        )}
        aria-hidden="true"
      >
        <Icon className="size-3.5" aria-hidden="true" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-body-sm font-medium text-text">{entry.name}</span>
        <span className="truncate text-caption text-text-3">{entry.note}</span>
      </span>
    </Link>
  )
}

function FilterChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string
  count: number
  active: boolean
  onClick: () => void
}): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-caption font-medium transition-colors duration-120",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
        active
          ? "border-transparent bg-text text-surface"
          : "border-border bg-surface text-text-2 hover:border-border-strong hover:text-text",
      )}
    >
      {label}
      <span
        className={cn(
          "tabular-nums text-[10.5px]",
          active ? "text-surface/70" : "text-text-3",
        )}
      >
        {count}
      </span>
    </button>
  )
}
